import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Maximize2, Minimize2, X, Send, Smile, Paperclip, Mic } from 'lucide-react';
import { useChatStore } from '../../hooks/useChatStore';
import { useAdhaWriteMode } from '../../hooks/useAdhaWriteMode';
import { EmojiPicker } from './EmojiPicker';
import { ConversationList } from './ConversationList';
import { ChatMessage } from './ChatMessage';
import { TypingIndicator } from './TypingIndicator';

interface ChatContainerProps {
  mode: 'floating' | 'fullscreen';
  onClose: () => void;
  onModeChange: () => void;
}

export function ChatContainer({ mode, onClose, onModeChange }: ChatContainerProps) {
  const {
    conversations,
    activeConversationId,
    isTyping,
    isRecording,
    isOnline,
    isLoading,
    error,
    fetchConversations,
    setRecording,
    addMessage,
    updateMessage,
    toggleLike,
    toggleDislike,
    createNewConversation,
    deleteConversation,
    setActiveConversation
  } = useChatStore();
  
  const adhaWriteMode = useAdhaWriteMode();

  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeConversation = conversations.find(c => c.id === activeConversationId);
  const messages = activeConversation?.messages || [];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Charger les conversations depuis l'API au montage du composant
  useEffect(() => {
    fetchConversations();
    
    // Ajouter des écouteurs d'événements pour la connectivité
    const handleOnline = () => {
      fetchConversations();
    };
    
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [fetchConversations]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    await addMessage(newMessage, 'user');
    setNewMessage('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target?.result as string;
      await addMessage('Pièce jointe envoyée', 'user', {
        name: file.name,
        type: file.type,
        content
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div 
      className={`
        bg-card flex flex-col
        ${mode === 'floating' 
          ? 'fixed bottom-4 right-4 w-[400px] h-[600px] rounded-lg shadow-xl' 
          : 'w-full h-full'
        }
        ${mode === 'fullscreen' ? 'rounded-none' : 'rounded-lg'}
      `}
      style={{
        maxWidth: mode === 'floating' ? '90vw' : '100vw',
        maxHeight: mode === 'floating' ? '90vh' : '100vh'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center space-x-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <h3 className="font-medium">ADHA Assistant</h3>
          {/* Indicateur de connexion */}
          <div className="ml-2 flex items-center">
            <div 
              className={`w-2 h-2 rounded-full mr-1 ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} 
              title={isOnline ? 'Connecté' : 'Mode hors ligne'}
            />
            {!isOnline && <span className="text-xs text-red-500">Hors ligne</span>}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onModeChange}
            className="text-muted-foreground hover:text-foreground"
          >
            {mode === 'floating' ? (
              <Maximize2 className="h-4 w-4" />
            ) : (
              <Minimize2 className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 px-4 py-2 text-sm text-red-800 flex justify-between items-center">
          <span>{error}</span>
          <button 
            onClick={() => useChatStore.setState({ error: null })}
            className="text-red-600 hover:text-red-800"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Indicateur de chargement */}
      {isLoading && (
        <div className="bg-blue-50 px-4 py-2 text-sm text-blue-800 text-center">
          Chargement des conversations...
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {mode === 'fullscreen' && (
          <ConversationList
            conversations={conversations}
            activeId={activeConversationId}
            onSelect={setActiveConversation}
            onDelete={deleteConversation}
            onNew={createNewConversation}
          />
        )}

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6 chat-scrollbar">
            {messages.map((message) => (
              <div key={message.id} className="message-enter">
                <ChatMessage
                  message={message}
                  onEdit={(messageId, content) => updateMessage(messageId, { content })}
                  onLike={toggleLike}
                  onDislike={toggleDislike}
                />
              </div>
            ))}
            
            {isTyping && (
              <TypingIndicator mode="thinking" />
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Sélecteur de mode */}
          <div className="relative border-t border-border py-2 px-4">
            {/* Toggle mode écriture */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Mode:</span>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-muted-foreground">Chat</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    checked={adhaWriteMode.isActive}
                    onChange={() => adhaWriteMode.toggle()}
                  />
                  <div className="w-11 h-6 bg-secondary peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                </label>
                <span className="text-xs text-muted-foreground">Écriture ADHA</span>
              </div>
            </div>
          </div>

          {/* Input */}
          <div className="p-4 border-t">
            <div className="flex flex-col space-y-2">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Écrivez votre message..."
                className="w-full pl-4 pr-4 py-2 border rounded-lg focus:ring-1 focus:ring-primary resize-none"
                rows={2}
              />
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-1.5 rounded-full bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground"
                    title="Joindre un fichier"
                  >
                    <Paperclip className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-1.5 rounded-full bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground"
                    title="Ajouter un emoji"
                  >
                    <Smile className="h-5 w-5" />
                  </button>
                  <button
                    onMouseDown={() => setRecording(true)}
                    onMouseUp={() => setRecording(false)}
                    className={`p-1.5 rounded-full bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground ${isRecording ? '!bg-red-100 !text-red-500 dark:!bg-red-900 dark:!text-red-400' : ''}`}
                    title="Enregistrer un message vocal"
                  >
                    <Mic className="h-5 w-5" />
                  </button>
                </div>
                <button
                  onClick={handleSend}
                  disabled={!newMessage.trim()}
                  className="px-4 py-1.5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                  title="Envoyer le message"
                >
                  <span>Envoyer</span>
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {showEmojiPicker && (
              <div className="absolute bottom-24 right-4 z-10 shadow-lg rounded-lg">
                <EmojiPicker
                  onSelect={(emoji) => {
                    setNewMessage(prev => prev + emoji);
                    setShowEmojiPicker(false);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}